/**
 * BuyPlanCard — the colour x size buy-plan matrix.
 *
 * Renders one editable row per colourway and one column per size (the UNION of
 * every colourway's sizes — size sets vary per colour, so a cell where a colour
 * doesn't carry that size is disabled/blank). Each cell is the planned quantity
 * for (colour, size), bound to a local draft and auto-saved on blur through the
 * SAME `setVariants` mutation the VariantsCard uses (via the shared
 * `setVariantsSerialized` queue below). Every save resends the FULL variant
 * array carrying colour / sizes / pantone / photo / swatch alongside the
 * quantities.
 *
 * Two correctness constraints drive the shape of this file:
 *
 *   1. Typing must stay smooth on large plans. Cell rows are memoized
 *      (`BuyPlanRow`) with stable callbacks, and each row only reads its own
 *      slice of the draft (`draft[row.id]`), which keeps referential identity
 *      when a DIFFERENT row is edited. So a keystroke re-renders one row's
 *      inputs, not the whole grid.
 *
 *   2. Overlapping saves — from THIS card's own blur-saves AND from
 *      VariantsCard's independent edits — must not lose data. Every save goes
 *      through `setVariantsSerialized` (`useProductDevMutations`), a SHARED
 *      queue both cards enqueue into in click order. Rather than pre-building
 *      the payload, `persist` hands the queue a BUILDER that re-reads the
 *      variants cache (`getCachedVariants`) at the moment it actually runs —
 *      so a save queued behind another card's edit always resends that edit's
 *      result instead of a stale snapshot taken when the save was requested.
 *      The Saving…/Saved indicator and the draft-resync guard below both
 *      derive from the shared `setVariantsQueueDepth` for the same reason: the
 *      cache isn't safe to resync from until every queued save (from either
 *      card) has drained.
 *
 * The $ line multiplies the grand total by the landed unit cost from the shared
 * `computeCommercials` (fed via the exported `productToCommercialsInput` mapping
 * the cost sheet uses), and hides itself when there is no cost yet.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { computeCommercials } from '@threadheads/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/contexts/OrganizationContext';
import { CardRowsSkeleton } from './skeletons';
import { pantoneToHex } from '../utils/pantone';
import type { useProductDevVariants, useProductDevMutations } from '../hooks/useProductDev';
import {
  productToCommercialsInput,
  type CommercialsProduct,
} from './CommercialsCard';

type QtyDraft = Record<string, Record<string, string>>;
type RowDraft = Record<string, string>;
type VariantRow = NonNullable<ReturnType<typeof useProductDevVariants>['data']>[number];

const onlyDigits = (s: string): string => s.replace(/[^0-9]/g, '');

const parseCell = (raw: string | undefined): number => {
  const n = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const fmtInt = (v: number): string => new Intl.NumberFormat().format(v);

// ----------------------------------------------------------------------------
// BuyPlanRow — one memoized colourway row. Reads only its own draft slice so
// editing a different row doesn't re-render (and re-reconcile) this row's
// inputs. All callbacks passed in are stable (see parent's useCallback).
// ----------------------------------------------------------------------------

const BuyPlanRow = memo(function BuyPlanRow({
  row,
  sizes,
  rowDraft,
  canEdit,
  moq,
  setCell,
  commitCell,
  t,
}: {
  row: VariantRow;
  sizes: string[];
  rowDraft: RowDraft | undefined;
  canEdit: boolean;
  moq: number | null | undefined;
  setCell: (id: string, size: string, raw: string) => void;
  commitCell: (id: string, size: string) => void;
  t: TFunction<'productDev'>;
}) {
  const total = row.sizes.reduce((acc, s) => acc + parseCell(rowDraft?.[s]), 0);
  const belowMoq = moq != null && total > 0 && total < moq;
  const swatch = pantoneToHex(row.pantone_code);
  // Same visual priority as the Variants card: uploaded fabric swatch → product
  // photo → pantone dot.
  const thumbUrl = row.swatch_url ?? row.photo_url;

  return (
    <tr className="border-b last:border-b-0">
      <td className="sticky left-0 z-10 bg-card py-2 pr-3 align-top">
        <div className="flex items-center gap-2">
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt=""
              loading="lazy"
              className="h-5 w-5 shrink-0 rounded-[5px] border border-border object-cover"
              aria-hidden="true"
            />
          ) : (
            <span
              className="h-3.5 w-3.5 shrink-0 rounded-full border border-border"
              style={{ backgroundColor: swatch ?? '#e5e7eb' }}
              aria-hidden="true"
            />
          )}
          <span className="text-sm font-medium truncate max-w-[10rem]">{row.colour}</span>
        </div>
        {belowMoq && (
          <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t('buyPlan.belowMoq', { moq })}
          </p>
        )}
      </td>
      {sizes.map((s) => {
        const has = row.sizes.includes(s);
        if (!has) {
          return (
            <td
              key={s}
              className="px-1 py-1.5 text-right text-muted-foreground/30"
              aria-hidden="true"
            >
              <span className="tabular-nums">·</span>
            </td>
          );
        }
        return (
          <td key={s} className="px-1 py-1.5 text-right">
            {canEdit ? (
              <Input
                inputMode="numeric"
                value={rowDraft?.[s] ?? ''}
                onChange={(e) => setCell(row.id, s, e.target.value)}
                onBlur={() => commitCell(row.id, s)}
                aria-label={t('buyPlan.cellAria', { colour: row.colour, size: s })}
                className="ml-auto h-8 w-16 px-2 text-right text-sm tabular-nums"
              />
            ) : (
              <span className="tabular-nums">
                {parseCell(rowDraft?.[s]) || <span className="text-muted-foreground">-</span>}
              </span>
            )}
          </td>
        );
      })}
      <td className="px-2 py-1.5 text-right text-sm font-medium tabular-nums">
        {total > 0 ? fmtInt(total) : <span className="text-muted-foreground">-</span>}
      </td>
    </tr>
  );
});

export function BuyPlanCard({
  productDevId,
  product,
  canEdit,
  variantsQuery,
  setVariantsSerialized,
  setVariantsQueueDepth,
  getCachedVariants,
}: {
  productDevId: string;
  product: CommercialsProduct;
  canEdit: boolean;
  variantsQuery: ReturnType<typeof useProductDevVariants>;
  setVariantsSerialized: ReturnType<typeof useProductDevMutations>['setVariantsSerialized'];
  setVariantsQueueDepth: ReturnType<typeof useProductDevMutations>['setVariantsQueueDepth'];
  getCachedVariants: ReturnType<typeof useProductDevMutations>['getCachedVariants'];
}) {
  const { t } = useTranslation('productDev');
  const { organization } = useOrganization();
  const rows = useMemo(() => variantsQuery.data ?? [], [variantsQuery.data]);

  // Column set = union of every colourway's sizes, in first-seen order.
  const sizes = useMemo(() => {
    const seen: string[] = [];
    for (const r of rows) {
      for (const s of r.sizes) if (!seen.includes(s)) seen.push(s);
    }
    return seen;
  }, [rows]);

  // Local draft of the editable cells, resynced whenever the server rows change
  // (add/remove colourway, rename, or an echoed save). Because every save writes
  // the whole draft, the echo is identical, so the resync is a no-op mid-edit.
  const signature = useMemo(
    () => JSON.stringify(rows.map((r) => [r.id, r.sizes, r.size_quantities])),
    [rows],
  );
  const buildDraft = useCallback(
    (): QtyDraft =>
      Object.fromEntries(
        rows.map((r) => [
          r.id,
          Object.fromEntries(
            r.sizes.map((s) => [s, r.size_quantities[s] ? String(r.size_quantities[s]) : '']),
          ),
        ]),
      ),
    [rows],
  );
  const [draft, setDraft] = useState<QtyDraft>(buildDraft);

  // Refs mirror the latest draft + rows so the serialized save path (below) and
  // the stable per-cell callbacks always read fresh values without being in
  // their dependency arrays (which would break memoization + serialization).
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Display-only mirror of the shared save queue — this state exists purely to
  // drive the header "Saving… / Saved" confidence indicator. 'fading' keeps
  // the "Saved" content mounted while its opacity transitions to 0, so it
  // fades out rather than snapping away.
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'fading'>('idle');
  const savedTimerRef = useRef<number | null>(null);
  const fadeTimerRef = useRef<number | null>(null);
  const wasQueueBusyRef = useRef(false);
  const clearSaveTimers = useCallback(() => {
    if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
    if (fadeTimerRef.current != null) window.clearTimeout(fadeTimerRef.current);
    savedTimerRef.current = null;
    fadeTimerRef.current = null;
  }, []);
  useEffect(() => clearSaveTimers, [clearSaveTimers]);

  // Drive Saving…/Saved off the SHARED queue depth (setVariantsSerialized in
  // useProductDevMutations), not a card-local flag — a save queued by
  // VariantsCard delays this card's own save just as much as one of its own,
  // so the indicator (and the resync guard below) need to see the whole
  // queue, not just this card's slice of it.
  useEffect(() => {
    const busy = setVariantsQueueDepth > 0;
    if (busy) {
      clearSaveTimers();
      setSaveState('saving');
    } else if (wasQueueBusyRef.current) {
      // Queue just fully drained — flip to "Saved" then fade out.
      setSaveState('saved');
      savedTimerRef.current = window.setTimeout(() => {
        setSaveState('fading');
        fadeTimerRef.current = window.setTimeout(() => setSaveState('idle'), 200);
      }, 1800);
    }
    wasQueueBusyRef.current = busy;
  }, [setVariantsQueueDepth, clearSaveTimers]);

  useEffect(() => {
    // Resync the draft from server rows on a genuine shape change, but NOT
    // while the shared queue is busy: a save queued by EITHER card echoes the
    // rows back through the cache, and resyncing mid-queue would discard
    // cells the user is mid-typing in a different column before that save
    // settles.
    if (setVariantsQueueDepth > 0) return;
    setDraft(buildDraft());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, setVariantsQueueDepth]);

  const currency =
    (product.agreed_currency ?? '') || organization?.default_currency || 'USD';
  const landed = useMemo(
    () => computeCommercials(productToCommercialsInput(product)).landed,
    [product],
  );

  const colTotal = (size: string): number =>
    rows.reduce((acc, r) => acc + (r.sizes.includes(size) ? parseCell(draft[r.id]?.[size]) : 0), 0);
  const grandTotal = rows.reduce(
    (acc, r) => acc + r.sizes.reduce((a, s) => a + parseCell(draft[r.id]?.[s]), 0),
    0,
  );

  const fmtMoney = (v: number): string => {
    try {
      return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v);
    } catch {
      return v.toFixed(2);
    }
  };

  // Persist the whole variant array by enqueuing a BUILDER into the shared
  // queue (setVariantsSerialized): the builder isn't invoked until this
  // save's turn actually comes up, so it reads `getCachedVariants` (the
  // freshest committed rows — including any colourway added/renamed/removed
  // by VariantsCard while this save was queued) rather than a snapshot taken
  // when the blur fired. `draftRef` still supplies THIS card's own in-flight
  // quantity edits, which never reach the server any other way. Falls back to
  // `rowsRef` if the cache is momentarily empty (shouldn't happen once the
  // grid has rendered at all). Errors are caught here so the queue never wedges.
  const persist = useCallback(async () => {
    try {
      await setVariantsSerialized(productDevId, () => {
        const currentRows = getCachedVariants(productDevId) ?? rowsRef.current;
        const nextDraft = draftRef.current;
        return currentRows.map((v, i) => {
          const q: Record<string, number> = {};
          for (const s of v.sizes) {
            const n = parseCell(nextDraft[v.id]?.[s]);
            if (n > 0) q[s] = n;
          }
          return {
            colour: v.colour,
            sizes: v.sizes,
            sort_order: i,
            pantone_code: v.pantone_code,
            photo_file_id: v.photo_file_id,
            swatch_file_id: v.swatch_file_id,
            size_quantities: q,
            // Carry per-colourway approval so a buy-plan save never clobbers a
            // status set in the Variants card.
            status: v.status,
          };
        });
      });
    } catch (err) {
      toast({
        title: t('buyPlan.toast.saveFailed'),
        description: err instanceof Error ? err.message : t('buyPlan.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  }, [productDevId, setVariantsSerialized, getCachedVariants, t]);

  const setCell = useCallback((id: string, size: string, raw: string) => {
    setDraft((d) => ({ ...d, [id]: { ...(d[id] ?? {}), [size]: onlyDigits(raw) } }));
  }, []);

  const commitCell = useCallback(
    (id: string, size: string) => {
      // Only persist when the committed value differs from what the server
      // holds, so tabbing across cells without changes doesn't fire redundant
      // saves. Reads from refs to stay stable across renders. Ordering across
      // rapid commits is guaranteed by the shared queue in `persist`, not by
      // any local single-flight guard here.
      const row = rowsRef.current.find((r) => r.id === id);
      if (!row) return;
      const current = parseCell(draftRef.current[id]?.[size]);
      const stored = row.size_quantities[size] ?? 0;
      if (current === stored) return;
      void persist();
    },
    [persist],
  );

  const moq = product.moq;

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-0.5">
          <CardTitle className="text-base">{t('buyPlan.title')}</CardTitle>
          {landed > 0 && grandTotal > 0 && (
            <p className="text-xs text-muted-foreground tabular-nums">
              {t('buyPlan.landedValue', { value: `${fmtMoney(grandTotal * landed)} ${currency}` })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Quiet auto-save confidence signal (Jack's ask): "Saving…" while a
              save is in flight or queued, then "Saved" for ~2s, then fades out.
              No manual save button — auto-save stays; this is the trust cue. */}
          <span
            role="status"
            aria-live="polite"
            className={cn(
              'inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-opacity duration-200 motion-reduce:transition-none',
              saveState === 'saving' || saveState === 'saved' ? 'opacity-100' : 'opacity-0',
            )}
          >
            {saveState === 'saving' ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                {t('buyPlan.saving')}
              </>
            ) : saveState === 'saved' || saveState === 'fading' ? (
              <>
                <Check className="h-3 w-3" aria-hidden="true" />
                {t('buyPlan.saved')}
              </>
            ) : null}
          </span>
          <span className="inline-flex h-8 items-center rounded-md bg-muted px-3 text-[11px] font-medium tabular-nums text-muted-foreground">
            {t('buyPlan.grandTotal', { n: fmtInt(grandTotal) })}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {variantsQuery.isLoading ? (
          <CardRowsSkeleton rows={2} />
        ) : variantsQuery.error ? (
          // A failed fetch must not masquerade as an empty plan — the operator
          // could start re-entering quantities that already exist.
          <p className="text-sm text-destructive">{t('buyPlan.loadError')}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('buyPlan.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b">
                  <th className="sticky left-0 z-10 bg-card py-2 pr-3 text-left text-xs font-medium text-muted-foreground">
                    {t('buyPlan.colour')}
                  </th>
                  {sizes.map((s) => (
                    <th
                      key={s}
                      className="px-2 py-2 text-right text-xs font-medium text-muted-foreground tabular-nums"
                    >
                      {s}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground">
                    {t('buyPlan.total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <BuyPlanRow
                    key={row.id}
                    row={row}
                    sizes={sizes}
                    rowDraft={draft[row.id]}
                    canEdit={canEdit}
                    moq={moq}
                    setCell={setCell}
                    commitCell={commitCell}
                    t={t}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t">
                  <td className="sticky left-0 z-10 bg-card py-2 pr-3 text-left text-xs font-medium text-muted-foreground">
                    {t('buyPlan.total')}
                  </td>
                  {sizes.map((s) => (
                    <td
                      key={s}
                      className="px-2 py-2 text-right text-sm font-medium tabular-nums text-muted-foreground"
                    >
                      {fmtInt(colTotal(s))}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right text-base font-semibold tabular-nums">
                    {fmtInt(grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
